// @ts-nocheck
import {
    ArchiveBoxArrowDownIcon,
    ArchiveBoxIcon,
    ArrowPathIcon,
    ChartBarIcon,
    BanknotesIcon,
    BarsArrowDownIcon,
    BuildingStorefrontIcon,
    CheckBadgeIcon,
    ClipboardDocumentCheckIcon,
    ClipboardDocumentListIcon,
    DocumentCurrencyEuroIcon,
    DocumentTextIcon,
    HomeIcon,
    LightBulbIcon,
    ListBulletIcon,
    DocumentMinusIcon,
    DocumentPlusIcon,
    RectangleStackIcon,
    ScaleIcon,
    TagIcon,
    UsersIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    DocumentCheckIcon
} from '@heroicons/react/24/outline'
import {Link} from "react-router-dom";
import {isMacOS} from "../../lib/platform.ts";
import {Role} from "../../api/accessControl/Role.ts";
import {CreditCardIcon} from "lucide-react";
import Banner from "../general/Banner.tsx";
import {BackendApi} from "../../api/api.ts";
import {Logo} from "./Logo.tsx";
import {UserBadge} from "./UserBadge.tsx";
import {SidebarSectionEnum} from "../../types/enums.ts";
import { useEffect, useState } from "react";
import { useQuinn, QUINN_PANEL_GUTTER } from "../../views/Quinn";
import { getSidebarCollapsed, setSidebarCollapsed } from "../../utils/sidebar.ts";
import { isViewingAsSubordinate } from "../../utils/OrgManagement.ts";
import RelativeLoadingFrame from "../RelativeLoadingFrame.tsx";
import { canUseInvoicing } from "../../lib/invoicingAccess.ts";
import { canSeeReports } from "../../lib/reportsAccess.ts";

type IconComponent = React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;

interface Route {
    name: string;
    href: string;
    icon: IconComponent;
    notification: boolean;
    isBeta: boolean;
    section: string;
    current?: boolean;
}

interface Notice {
    body?: string;
    action_url?: string;
}

export interface AddRouteOptions {
    notification?: boolean;
    isBeta?: boolean;
    section?: SidebarSectionEnum;
}

class Navigation {
    private routes: Route[];
    private currentUrlPath: string;

    constructor(currentUrlPath: string) {
        this.routes = [];
        this.currentUrlPath = currentUrlPath;
    }

    public addRoute(name: string, href: string, icon: IconComponent, options?: AddRouteOptions): void {
        this.routes.push({
            name,
            href,
            icon,
            notification: options?.notification ?? false,
            isBeta: options?.isBeta ?? false,
            section: options?.section ?? SidebarSectionEnum.UNGROUPED
        })
    }

    public getAllRoutes(): Route[] {
        return this.routes.map((old) => {
            return { ...old, current: this.currentUrlPath === old.href }
        })
    }

    public getRoutesBySection(): { [key: string]: Route[] } {
        const routes = this.getAllRoutes();
        const grouped: { [key: string]: Route[] } = {};

        routes.forEach(route => {
            const section = route.section;
            if (!grouped[section]) {
                grouped[section] = [];
            }
            grouped[section].push(route);
        });

        return grouped;
    }
}

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

async function areThereUnreadInsights(api: BackendApi): Promise<boolean> {
    const response = await api.Insights__unreadInsights({})
    return response.ids.length > 0;
}

function PoweredByPocketCFO() {
    return <div className="m-2">
        <span
            className="flex justify-center gap-1 items-center py-2 rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
            <svg className="h-4" viewBox="0 0 152 151" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.345703" y="0.759796" width="151.613" height="150" rx="7" fill="#45AEEA" />
                <path
                    d="M71.4641 43.0025C69.8453 41.3837 70.9918 38.6159 73.281 38.6159H110.219C111.638 38.6159 112.789 39.7663 112.789 41.1855V78.1235C112.789 80.4128 110.021 81.5593 108.402 79.9405L71.4641 43.0025Z"
                    fill="white" />
                <path
                    d="M48.6085 65.8581C46.9897 64.2393 48.1362 61.4715 50.4254 61.4715H87.3635C88.7826 61.4715 89.9331 62.6219 89.9331 64.0411V100.979C89.9331 103.268 87.1653 104.415 85.5465 102.796L48.6085 65.8581Z"
                    fill="white" />
                <path
                    d="M25.2613 88.7137C23.6425 87.0949 24.789 84.3271 27.0783 84.3271H64.0163C65.4355 84.3271 66.5859 85.4775 66.5859 86.8967V123.835C66.5859 126.124 63.8181 127.27 62.1994 125.652L25.2613 88.7137Z"
                    fill="white" />
            </svg> Powered by PocketCFO

        </span>
    </div>;
}

const IS_MAC = isMacOS();

interface SidebarProps {
    nav: Navigation;
    firstName: string;
    lastName: string;
    email: string;
    role: Role;
    businessName: string;
    access: Array<{ business_id: string; business_name: string }>;
    showQuinnShortcut: boolean;
    isCollapsed: boolean;
    isHovered: boolean;
    onOpenQuinn: () => void;
    setIsCollapsed: (collapsed: boolean) => void;
    setIsHovered: (hovered: boolean) => void;
}

function Sidebar({
    nav,
    firstName,
    lastName,
    email,
    role,
    businessName,
    access,
    showQuinnShortcut,
    isCollapsed,
    isHovered,
    onOpenQuinn,
    setIsCollapsed,
    setIsHovered,
}: SidebarProps) {
    const routesBySection = nav.getRoutesBySection();
    const sectionOrder = Object.values(SidebarSectionEnum);

    const isExpanded = !isCollapsed || isHovered;

    return <div
        className={classNames(
            "fixed inset-y-0 left-0 z-40 flex flex-col transition-all duration-300 ease-in-out bg-gray-50",
            isExpanded ? "w-72" : "w-20",
            isCollapsed && isHovered && "shadow-[4px_0_18px_-6px_rgba(0,0,0,0.12)]",
            isViewingAsSubordinate() && "mt-[4.5rem]"
        )}
        onMouseEnter={() => isCollapsed && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
    >
        <div className="flex grow flex-col z-50 overflow-y-auto overflow-x-hidden relative">
            <div className={classNames(
                "flex h-20 shrink-0 items-center justify-between px-6 transition-all duration-300",
                isExpanded ? "justify-between" : "justify-center"
            )}>
                <Link to={"/"} className="flex items-center overflow-hidden">
                    <Logo collapsed={!isExpanded} />
                </Link>
                {isExpanded && (
                    <button
                        onClick={() => {
                            setIsCollapsed(!isCollapsed);
                            setIsHovered(false);
                        }}
                        className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                    >
                        {!isCollapsed ? (
                            <ChevronDoubleLeftIcon className="h-5 w-5" />
                        ) : (
                            <ChevronDoubleRightIcon className="h-5 w-5" />
                        )}
                    </button>
                )}
            </div>
            <nav className="flex flex-1 flex-col mt-4">
                <ul role="list" className="flex flex-1 flex-col gap-y-1">
                    {sectionOrder.map((sectionName) => {
                        const routes = routesBySection[sectionName] || [];
                        if (routes.length === 0) return null;

                        return (
                            <li key={sectionName}>
                                {sectionName !== SidebarSectionEnum.UNGROUPED && isExpanded && (
                                    <div className="px-4 py-2">
                                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-4 whitespace-nowrap">
                                            {sectionName}
                                        </h3>
                                    </div>
                                )}
                                <ul role="list" className={classNames("space-y-1", isExpanded ? "px-4" : "px-2")}>
                                    {routes.map((item) => (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            className={classNames(
                                                item.current
                                                    ? 'bg-gray-50 text-primary'
                                                    : 'text-gray-700 hover:bg-gray-50 hover:text-primary',
                                                'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 relative transition-all duration-300',
                                                isExpanded ? "" : "justify-center"
                                            )}
                                            title={!isExpanded ? item.name : ""}
                                        >
                                            <item.icon
                                                className={classNames(
                                                    item.current ? 'text-primary' : 'text-gray-400 group-hover:text-primary',
                                                    'h-6 w-6 shrink-0',
                                                )}
                                                aria-hidden="true"
                                            />
                                            {isExpanded && (
                                                <span className="relative inline-block truncate">
                                                    {item.name}
                                                    {item.notification && (
                                                        <span
                                                            className="absolute top-1 -right-2 h-2 w-2 rounded-full bg-red-500"></span>
                                                    )}
                                                    {item.isBeta && (
                                                        <span
                                                            className="ml-2 inline-flex items-center rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">BETA</span>
                                                    )}
                                                </span>
                                            )}
                                            {!isExpanded && item.notification && (
                                                <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-red-500"></span>
                                            )}
                                        </Link>
                                    ))}
                                </ul>
                            </li>
                        );
                    })}

                    {isExpanded && (
                        <li className="mt-4">
                            {globalThis.isWhiteLabeled && <PoweredByPocketCFO />}
                        </li>
                    )}

                    {showQuinnShortcut && (
                        <li className={classNames(
                            isExpanded ? 'px-4 pb-3' : 'px-2 pb-3',
                            'mt-auto',
                        )}>
                            <button
                                type="button"
                                className="w-full"
                                onClick={onOpenQuinn}
                                title={!isExpanded ? "Vraag Quinn" : ""}
                            >
                                <div
                                    className={classNames(
                                        "flex items-center rounded-lg bg-gray-50 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-100 transition-all duration-300",
                                        isExpanded ? "justify-between px-4 py-3" : "justify-center p-2"
                                    )}
                                >
                                    {isExpanded ? (
                                        <>
                                            <span>Vraag Quinn</span>
                                            <span
                                                className="flex items-center gap-1 text-[10px] font-semibold text-gray-500"
                                            >
                                                <span
                                                    className="rounded border border-gray-200 bg-white px-1.5 py-0.5 leading-none"
                                                >
                                                    {IS_MAC ? '⌘' : 'Ctrl'}
                                                </span>
                                                <span
                                                    className="rounded border border-gray-200 bg-white px-1.5 py-0.5 leading-none"
                                                >
                                                    K
                                                </span>
                                            </span>
                                        </>
                                    ) : (
                                        <LightBulbIcon className="h-6 w-6 text-gray-400" />
                                    )}
                                </div>
                            </button>
                        </li>
                    )}

                    <li
                        className={classNames(
                            showQuinnShortcut ? '' : 'mt-auto',
                            'hover:bg-gray-50 cursor-pointer border-t border-gray-100 pb-4 pt-3 transition-all duration-300',
                            isExpanded ? 'px-4' : 'px-2 flex justify-center'
                        )}
                    >
                        <UserBadge firstName={firstName} lastName={lastName}
                            email={email} role={role}
                            businessName={businessName}
                            access={access}
                            collapsed={!isExpanded} />

                    </li>

                </ul>
            </nav>
        </div>
    </div>;
}

export default function Shell({ currentUser, isLoading = false, title = null, subtitle = null, titleActions = null, children }) {
    const [thereAreUnreadInsights, setThereAreUnreadInsights] = useState<boolean>(false);
    const [notices, setNotices] = useState<Notice[]>([])
    const quinn = useQuinn();

    const [isCollapsed, setIsCollapsed] = useState(() => getSidebarCollapsed());
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        setSidebarCollapsed(isCollapsed);
    }, [isCollapsed]);

    const backendApi = new BackendApi(currentUser.getToken())

    async function load() {
        try {
            const response = await backendApi.Me__notices({})
            const noticesArray = Array.isArray(response) ? response : [];
            setNotices(noticesArray)
        } catch (err) {
            console.error('Error loading notices:', err);
            setNotices([])
        }

        if (currentUser.role === Role.Entrepreneur) {
            try {
                const unreadInsights = await areThereUnreadInsights(backendApi)
                setThereAreUnreadInsights(unreadInsights)
            } catch (err) {
                console.error('Error loading unread insights:', err);
                setThereAreUnreadInsights(false)
            }
        }

    }

    useEffect(() => {
        load()
    }, [])

    const nav = new Navigation(new URL(document.URL).pathname)

    const canUseQuinnShortcut = currentUser.role === Role.Partner && currentUser.flags.getFlag('PARTNER__CAN_SEE_QUINN');
    const quinnIsPushing = Boolean(quinn?.isOpen && !quinn.isOverlay);
    const quinnPushWidth = quinnIsPushing && quinn ? quinn.width + QUINN_PANEL_GUTTER * 2 : 0;

    useEffect(() => {
        if (!canUseQuinnShortcut) return;

        function onKeyDown(e: KeyboardEvent) {
            const isMac = IS_MAC;
            const isShortcut = (isMac ? e.metaKey : e.ctrlKey) && (e.key === 'k' || e.key === 'K');

            if (isShortcut) {
                e.preventDefault();
                quinn?.toggle();
            }
        }

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [canUseQuinnShortcut, quinn?.toggle]);

    if (currentUser.role === Role.Admin) {
        nav.addRoute('Dashboard', '/', HomeIcon)
        nav.addRoute('Categorise Transactions', '/base-transactions', TagIcon)
        nav.addRoute('Export', '/export', BarsArrowDownIcon)
        nav.addRoute('Users', '/users-list', UsersIcon)
        nav.addRoute('Businesses', '/businesses', BuildingStorefrontIcon)
        nav.addRoute('Import Jobs', '/import-jobs', ArrowPathIcon)
        nav.addRoute('Demo Partner', '/admin/demo-partner', BuildingStorefrontIcon)
        nav.addRoute('Document search', '/document-search', DocumentTextIcon)
    } else if (currentUser.role === Role.Entrepreneur) {
        nav.addRoute('Dashboard', '/', HomeIcon)
        nav.addRoute('Insights', '/insights', LightBulbIcon, { notification: thereAreUnreadInsights })

        nav.addRoute('Cashflow', '/cash-flow', BanknotesIcon, { section: SidebarSectionEnum.CASH_FLOW })
        nav.addRoute('Saldo', '/balance', ScaleIcon, { section: SidebarSectionEnum.CASH_FLOW })
        nav.addRoute('Transacties', '/transactions', ListBulletIcon, { section: SidebarSectionEnum.CASH_FLOW })

        if (currentUser.flags.getFlag('CAN_SEE_DOCUMENT_COLLECTION')) {
            nav.addRoute('Upload', '/share-documents', DocumentTextIcon, { section: SidebarSectionEnum.INVOICING })
        }

        if (canUseInvoicing(currentUser.businesses)) {
            nav.addRoute('Factuur maken', '/create-invoice', DocumentCurrencyEuroIcon, { section: SidebarSectionEnum.INVOICING })
            nav.addRoute('Klanten', '/customers', UsersIcon, { section: SidebarSectionEnum.INVOICING })
            nav.addRoute('Producten & diensten', '/products-and-services', RectangleStackIcon, { section: SidebarSectionEnum.INVOICING })
            nav.addRoute('Terugkerende facturen', '/recurring-invoices', ArrowPathIcon, { section: SidebarSectionEnum.INVOICING })

            nav.addRoute('Te ontvangen', '/outstanding-sales-invoices', DocumentPlusIcon, { section: SidebarSectionEnum.INVOICING })
            nav.addRoute('Te betalen', '/outstanding-purchase-invoices', DocumentMinusIcon, { section: SidebarSectionEnum.INVOICING })
        }

        if (canSeeReports(currentUser)) {
            nav.addRoute('Rapportage', '/reports', ChartBarIcon, { section: SidebarSectionEnum.OTHER })
        }

        nav.addRoute('Belastingen', '/taxes', ArchiveBoxIcon, { section: SidebarSectionEnum.OTHER })
        nav.addRoute('Bankrekeningen', '/banks', CreditCardIcon, { section: SidebarSectionEnum.OTHER })
    } else if (currentUser.role === Role.Partner) {
        nav.addRoute('Overzicht', '/', HomeIcon)
        nav.addRoute('Te controleren', '/documents-to-check', DocumentCheckIcon)
        nav.addRoute('Documenten', '/documents', DocumentTextIcon)
        nav.addRoute('Transacties', '/transactions', ListBulletIcon)
        if (currentUser.flags.getFlag('PARTNER__CAN_SEE_UNMATCHED_TRANSACTIONS'))
            nav.addRoute('Vraagposten', '/unmatched-transactions', DocumentTextIcon)

        if (currentUser.flags.getFlag('PARTNER__CAN_SEE_MATCHES'))
            nav.addRoute('Matches', '/matches', ClipboardDocumentListIcon)

        if (currentUser.flags.getFlag('PARTNER__CAN_ARCHIVE_DOCUMENTS'))
            nav.addRoute('Archief', '/archived-documents', ArchiveBoxArrowDownIcon)

    } else if (currentUser.role === Role.RO_Developer) {
        nav.addRoute('Document search', '/', DocumentTextIcon)
    }


    return (
        <>

            <div className='h-full flex relative bg-gray-50'>
                <Sidebar
                    nav={nav}
                    firstName={currentUser.firstName}
                    lastName={currentUser.lastName}
                    email={currentUser.email}
                    role={currentUser.role}
                    businessName={currentUser.businessName}
                    showQuinnShortcut={canUseQuinnShortcut}
                    onOpenQuinn={() => quinn?.toggle()}
                    access={currentUser.access}
                    isCollapsed={isCollapsed}
                    isHovered={isHovered}
                    setIsCollapsed={setIsCollapsed}
                    setIsHovered={setIsHovered}
                />


                <main
                    style={{ paddingRight: quinnPushWidth }}
                    className={classNames(
                        "z-10 flex-1 min-w-0 min-h-0 h-full overflow-y-auto transition-all duration-300 ease-in-out bg-transparent",
                        !isCollapsed ? "pl-72" : "pl-20"
                    )}>
                    <div className="flex flex-col min-h-full pt-8 pl-6">
                        {!!notices && notices.map((notice) => (
                            <div className='mb-8'><Banner content={notice?.body}
                                actionUrl={notice?.action_url} /></div>))}
                        {!!title && (
                            <div className="px-4 sm:px-6 lg:px-8 pb-6 shrink-0">
                                {typeof title === "string"
                                    ? <div className="flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <h1 className="text-4xl font-normal tracking-tight text-gray-900">{title}</h1>
                                            {!!subtitle && <p className="mt-2 text-sm text-gray-600">{subtitle}</p>}
                                        </div>
                                        {!!titleActions && <div className="shrink-0">{titleActions}</div>}
                                    </div>
                                    : title}
                            </div>
                        )}
                        <div
                            className={classNames(
                                "flex-1 min-h-0 bg-white rounded-tl-3xl shadow-[-8px_0_24px_-8px_rgba(0,0,0,0.10)] transition-[border-radius] duration-300 ease-in-out",
                                quinnIsPushing && "rounded-tr-3xl",
                            )}
                        >
                            <div className="px-4 sm:px-6 lg:px-8 relative py-10 max-w-full">

                                <RelativeLoadingFrame isLoading={isLoading}>
                                    {children}
                                </RelativeLoadingFrame>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </>
    )
}
